import { Box, Text, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { EntitySelect } from '@factorialco/factorial-one-react/dist/experimental'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Groups, WizardData } from '../../types'
import { useBenefitsEmployeeSelector } from './useBenefitsEmployeeSelector'

interface EntityBenefitSelectorProps {
  onEmployeesSelected: (employees: EmployeeForSelectFragmentNoAccess[]) => void
  employeeIdsWithBenefits: number[]
  companyId: number
}

const EntityBenefitSelector = ({
  onEmployeesSelected,
  employeeIdsWithBenefits,
  companyId,
}: EntityBenefitSelectorProps) => {
  const {
    loading,
    selectedGroup,
    groupMap,
    selectedGroupMap,
    onItemExpandedChange,
    onSelect,
    setSelectedGroup,
  } = useBenefitsEmployeeSelector(companyId, employeeIdsWithBenefits, onEmployeesSelected)

  const onGroupChange = (value: string | null) => {
    switch (value) {
      case Groups.ALL:
        setSelectedGroup(Groups.ALL)
        break
      case Groups.LEGAL_ENTITIES:
        setSelectedGroup(Groups.LEGAL_ENTITIES)
        break
    }
  }

  return (
    <Box width="s360" position="relative">
      <EntitySelect
        hiddenAvatar={false}
        loading={loading}
        entities={groupMap[selectedGroup] || []}
        triggerPlaceholder={i18n.t('employee_selector.select_employees')}
        triggerSelected={i18n.t('employee_selector.employees_selected')}
        selectedEntities={selectedGroupMap[selectedGroup]}
        searchPlaceholder={i18n.t('employee_selector.search')}
        selectAllLabel={i18n.t('employee_selector.select_all')}
        clearLabel={i18n.t('employee_selector.clear')}
        selectedLabel={i18n.t('employee_selector.selected')}
        selectedGroup={selectedGroup}
        onOpenChange={() => {}}
        onItemExpandedChange={(id, expanded) => onItemExpandedChange(Number(id), expanded)}
        onGroupChange={onGroupChange}
        groups={[
          { label: i18n.t('employee_selector.groups.none'), value: 'all' },
          {
            label: i18n.t(
              'benefits.tabs.employees.activation_results.modal.select_employees.groups.legal_entities'
            ),
            value: 'legalEntities',
          },
        ]}
        onSelect={onSelect}
        notFoundSubtitle={i18n.t('employee_selector.not_found.subtitle')}
        notFoundTitle={i18n.t('employee_selector.not_found.title')}
        singleSelector={false}
        alwaysOpen
        width={360}
        disabled={false}
        applySearchToGroup={undefined}
      />
    </Box>
  )
}

interface BenefitsEmployeeSelectorStepProps extends WizardStepRenderParams<WizardData> {
  employeeIdsWithBenefits: number[]
}

export const BenefitsEmployeeSelectorStep = ({
  data,
  onDataChanged,
  employeeIdsWithBenefits,
}: BenefitsEmployeeSelectorStepProps) => {
  const { company } = useCurrentSession()

  const handleEmployeesSelected = (selectedEmployees: EmployeeForSelectFragmentNoAccess[]) => {
    const employeeDetailsMap: Record<
      number,
      { firstName: string; lastName: string; avatarUrl?: string }
    > = {}
    selectedEmployees.forEach((employee) => {
      employeeDetailsMap[employee.id] = {
        firstName: employee.firstName,
        lastName: employee.lastName,
        avatarUrl: employee.access?.avatar || undefined,
      }
    })

    onDataChanged({
      ...data,
      employees_selected: selectedEmployees,
      employee_details_map: employeeDetailsMap,
    })
  }

  useEffect(() => {
    track(trackingEvents.benefits.userLandsInBenefitsActivationEmployeeSelector, {
      companyId: company.id,
    })
  }, [company.id])

  return (
    <Box flexDirection="column" position="relative" justifyContent="center">
      <Box flexDirection="column" paddingBottom="s20">
        <Text.Normal weight="semibold" color="grey1000" size="400">
          {i18n.t('benefits.tabs.employees.activation_results.modal.select_employees.step_title')}
        </Text.Normal>
        <Text.Normal weight="normal" color="grey800" size="300">
          {i18n.t('benefits.tabs.employees.activation_results.modal.select_employees.description')}
        </Text.Normal>
      </Box>
      <EntityBenefitSelector
        onEmployeesSelected={handleEmployeesSelected}
        employeeIdsWithBenefits={employeeIdsWithBenefits}
        companyId={company.id}
      />
    </Box>
  )
}

export const validateBenefitsEmployeeSelectorStep = (data: WizardData): boolean => {
  return data.employees_selected.length > 0
}
